import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';

type Deal = Database['public']['Tables']['deals']['Row'];

interface DealWithContact extends Deal {
  contact: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    primary_email: string | null;
    primary_phone: string | null;
    primary_phone_country_code: string | null;
  } | null;
  creator: {
    id: string;
    display_name: string | null;
    email: string | null;
  } | null;
  sold_by: {
    id: string;
    display_name: string | null;
    email: string | null;
  } | null;
  sales_dev: {
    id: string;
    display_name: string | null;
    email: string | null;
  } | null;
}

interface UseDealsSearchOptions {
  enabled?: boolean;
  debounceMs?: number;
  limit?: number;
}

export function useDealsSearch(query: string, options: UseDealsSearchOptions = {}) {
  const { enabled = true, debounceMs = 300, limit = 50 } = options;
  const [results, setResults] = useState<DealWithContact[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    // Clear previous timeout
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Abort previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Clear results if query is too short
    if (!query || query.trim().length < 2 || !enabled) {
      setResults([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    // Debounce the search
    debounceRef.current = setTimeout(async () => {
      try {
        abortControllerRef.current = new AbortController();
        
        console.log('🔍 [useDealsSearch] Buscando:', query.trim());

        // Use RPC for accent-insensitive deal name search
        const { data: dealsFromRpc, error: rpcError } = await supabase
          .rpc('search_deals_unaccent', {
            search_term: query.trim(),
            max_results: limit
          });

        if (rpcError) throw rpcError;

        // Get deal IDs from RPC results
        const dealIds = (dealsFromRpc || []).map((d: any) => d.id);

        // Also search contacts using RPC for accent-insensitive search
        const { data: matchingContacts, error: contactsError } = await supabase
          .rpc('search_contacts_unaccent', {
            search_term: query.trim(),
            max_results: 100
          });

        if (contactsError) throw contactsError;

        // Get contact IDs
        const contactIds = (matchingContacts || []).map((c: any) => c.id);

        // Fetch full deal data for deals from RPC results
        let dealsData: DealWithContact[] = [];
        if (dealIds.length > 0) {
          const { data, error } = await supabase
            .from('deals')
            .select(`
              *,
              contact:contacts!fk_deals_contact(
                id,
                first_name,
                last_name,
                primary_email,
                primary_phone,
                primary_phone_country_code
              ),
              creator:app_users!created_by(
                id,
                display_name,
                email
              ),
              sold_by:app_users!sales_consultant(
                id,
                display_name,
                email
              ),
              sales_dev:app_users!sales_development(
                id,
                display_name,
                email
              )
            `)
            .in('id', dealIds)
            .order('created_at', { ascending: false })
            .limit(limit);
          if (error) throw error;
          dealsData = (data || []) as DealWithContact[];
        }

        // Fetch deals by contact match
        let contactDeals: DealWithContact[] = [];
        if (contactIds.length > 0) {
          const { data, error } = await supabase
            .from('deals')
            .select(`
              *,
              contact:contacts!fk_deals_contact(
                id,
                first_name,
                last_name,
                primary_email,
                primary_phone,
                primary_phone_country_code
              ),
              creator:app_users!created_by(
                id,
                display_name,
                email
              ),
              sold_by:app_users!sales_consultant(
                id,
                display_name,
                email
              ),
              sales_dev:app_users!sales_development(
                id,
                display_name,
                email
              )
            `)
            .in('contact_id', contactIds)
            .order('created_at', { ascending: false })
            .limit(limit);

          if (error) throw error;
          contactDeals = (data || []) as DealWithContact[];
        }

        // Merge results, removing duplicates by id
        const allDeals = [...dealsData, ...contactDeals];
        const uniqueDeals = allDeals.reduce((acc, deal) => {
          if (!acc.find(d => d.id === deal.id)) {
            acc.push(deal);
          }
          return acc;
        }, [] as DealWithContact[]);

        // Sort by created_at desc and limit
        uniqueDeals.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        const finalResults = uniqueDeals.slice(0, limit);

        console.log('✅ [useDealsSearch] Resultados:', finalResults.length, 'deals');
        setResults(finalResults);
        setError(null);
      } catch (err) {
        if ((err as Error).name === 'AbortError') {
          return; // Ignore aborted requests
        }
        console.error('❌ [useDealsSearch] Erro:', err);
        setError(err as Error);
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, debounceMs);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [query, enabled, debounceMs, limit]);

  return {
    results,
    loading,
    error,
    isSearching: query.trim().length >= 2
  };
}
